import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChefHat, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { BrandMark } from "@/components/Brand";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const forced = user?.must_change_password;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated");
      await refresh();
      const r = user?.role;
      nav(r === "admin" ? "/admin" : r === "chef" ? "/chef" : "/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[hsl(var(--muted))]">
        <img src="https://images.pexels.com/photos/5792480/pexels-photo-5792480.jpeg" className="absolute inset-0 w-full h-full object-cover opacity-85" alt="ingredients"/>
        <div className="absolute inset-0 bg-gradient-to-br from-[hsla(20,3%,17%,0.45)] to-transparent"/>
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center"><BrandMark size={36}/></div>
          </div>
          <p className="heading text-3xl font-semibold max-w-md leading-tight">
            {forced ? "Welcome back. Set a fresh password to continue." : "Update your password anytime."}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          {forced && (
            <div className="rounded-xl border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="forced-banner">
              <ShieldCheck size={16} className="mt-0.5 shrink-0"/>
              <div>
                Your admin issued a one-time password. Please set your own password before continuing.
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" data-testid="change-form">
            <div>
              <h1 className="heading text-3xl font-bold">{forced ? "Set your password" : "Change password"}</h1>
              <p className="text-sm text-muted-foreground mt-1">Signed in as <b>{user?.email}</b></p>
            </div>

            <div>
              <Label>{forced ? "Temporary password" : "Current password"}</Label>
              <div className="relative">
                <Input required type={show ? "text" : "password"} value={cur} onChange={e=>setCur(e.target.value)} className="pr-11" data-testid="change-cur"/>
                <button type="button" onClick={()=>setShow(s=>!s)} className="absolute inset-y-0 right-0 px-3 flex items-center text-muted-foreground hover:text-primary" aria-label="toggle">
                  {show ? <EyeOff size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>

            <div>
              <Label>New password</Label>
              <Input required type={show ? "text" : "password"} value={pw} onChange={e=>setPw(e.target.value)} data-testid="change-pw"/>
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input required type={show ? "text" : "password"} value={confirm} onChange={e=>setConfirm(e.target.value)} data-testid="change-confirm"/>
            </div>

            <Button type="submit" disabled={busy} className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="change-submit">
              {busy ? "Updating..." : "Update password"}
            </Button>

            {!forced && (
              <button type="button" onClick={()=>nav(-1)} className="block w-full text-center text-sm text-muted-foreground hover:text-primary">Cancel</button>
            )}
            {forced && (
              <button type="button" onClick={()=>{logout(); nav("/login");}} className="block w-full text-center text-sm text-muted-foreground hover:text-primary">Sign out</button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
